import * as reactRouterDom from 'react-router-dom';
import SuperTokens from 'supertokens-auth-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import { getSuperTokensRoutesForReactRouterDom } from 'supertokens-auth-react/ui';
import { EmailPasswordPreBuiltUI } from 'supertokens-auth-react/recipe/emailpassword/prebuiltui';
import { EmailVerificationPreBuiltUI } from 'supertokens-auth-react/recipe/emailverification/prebuiltui';

import EmailVerification from 'supertokens-auth-react/recipe/emailverification';
import Session from 'supertokens-auth-react/recipe/session';
import { useStore, type UserProfile, initialPermissions, type UserPermissions } from '../store/Store';
import { getJson, isApiError } from './httpClient';
import { userPath, apiDomain, websiteDomain, serverPath, clientRoutes } from '../ApiPaths';
import { subscribeToNewsletter } from '../Api';
import { getSafeInternalReturnPath } from './Utility';

const environment = import.meta.env.MODE;

/** SessionStorage key for SSO return_url + state (used when redirecting back to customer site after login). */
export const SSO_REDIRECT_STORAGE_KEY = 'robotop_sso_return';

/** postMessage type from Login popup → partner opener (tokens for Unity iframe). */
export const ROBOTOP_POPUP_LOGIN_MSG = 'robotop-popup-login';

/** postMessage type parent → Unity iframe (same as Ecom / index.html). */
export const ROBOTOP_UNITY_SESSION_MSG = 'robotop-unity-session';

export type SsoReturnPayload = { return_url: string; state: string };

/** Persist partner SSO return target (called from AppLayout / Login). */
export function storeSsoReturn(returnUrl: string, state: string): void {
  sessionStorage.setItem(
    SSO_REDIRECT_STORAGE_KEY,
    JSON.stringify({ return_url: returnUrl, state } satisfies SsoReturnPayload)
  );
}

export function peekSsoReturn(): SsoReturnPayload | null {
  try {
    const stored = sessionStorage.getItem(SSO_REDIRECT_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { return_url?: string; state?: string };
    if (parsed.return_url && parsed.state) {
      return { return_url: parsed.return_url, state: parsed.state };
    }
  } catch {
    // ignore
  }
  return null;
}

export function clearSsoReturn(): void {
  sessionStorage.removeItem(SSO_REDIRECT_STORAGE_KEY);
}

/** Same-origin return_url (e.g. /dev/sso-demo) — safe without server allowlist. */
export function isSameOriginReturnUrl(returnUrl: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(returnUrl).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * After login: if a valid partner SSO return is pending, go to /login/return.
 * Otherwise use internal returnTo or dashboard.
 */
export async function resolvePostLoginPath(internalReturnTo?: string | null): Promise<string> {
  const sso = peekSsoReturn();
  if (sso) {
    // Same-app callbacks work even if the API allowlist is empty / server not restarted.
    if (isSameOriginReturnUrl(sso.return_url)) {
      return clientRoutes.loginReturn;
    }
    try {
      const checkUrl = `${serverPath}auth/redirect-allowed?return_url=${encodeURIComponent(sso.return_url)}`;
      const data = await getJson<{ allowed: boolean }>(checkUrl);
      if (data.allowed) {
        return clientRoutes.loginReturn;
      }
      console.warn('SSO return_url not in allowlist:', sso.return_url);
    } catch (error) {
      console.warn('SSO redirect-allowed check failed:', error);
    }
    clearSsoReturn();
  }
  return getSafeInternalReturnPath(internalReturnTo ?? null) ?? clientRoutes.dashboard;
}

const defaultPermissionsFor = (userId: string): UserPermissions => ({
  ...initialPermissions,
  _id: userId,
});

/** Bumped on each profile load and on logout so a slower response cannot overwrite the next user. */
let profileLoadGeneration = 0;

export function invalidateAuthenticatedProfileLoads(): void {
  profileLoadGeneration += 1;
}

async function isCurrentProfileLoad(userId: string, generation: number): Promise<boolean> {
  if (generation !== profileLoadGeneration) return false;
  try {
    if (!(await Session.doesSessionExist())) return false;
    if (generation !== profileLoadGeneration) return false;
    const sessionUserId = await Session.getUserId();
    return generation === profileLoadGeneration && sessionUserId === userId;
  } catch {
    return false;
  }
}

/** Load Mongo profile + permissions after SuperTokens auth. Never wipes the session on 404. */
export async function loadAuthenticatedUserProfile(userId: string, fallbackEmail?: string) {
  const generation = ++profileLoadGeneration;
  if (!(await isCurrentProfileLoad(userId, generation))) return;

  const state = useStore.getState();
  if (state.userProfile._id !== userId) {
    state.reset();
  }
  if (!(await isCurrentProfileLoad(userId, generation))) return;

  useStore.getState().setUserProfile({
    _id: userId,
    ...(fallbackEmail ? { email: fallbackEmail } : {}),
  });

  try {
    const data = await getJson<Partial<UserProfile>>(userPath + userId);
    if (!(await isCurrentProfileLoad(userId, generation))) return;
    if (environment === 'development') console.log('user profile', data);
    useStore.getState().setUserProfile({ ...data, _id: userId });
  } catch (error) {
    if (!(await isCurrentProfileLoad(userId, generation))) return;
    if (isApiError(error) && error.response.status === 404) {
      console.warn('Mongo user profile not found yet for', userId);
    } else {
      console.error('Failed to load user profile:', error);
    }
  }

  try {
    const permissions = await getJson<UserPermissions | null>(
      `${userPath}${userId}/permissions`
    );
    if (!(await isCurrentProfileLoad(userId, generation))) return;
    useStore.getState().setUserPermissions(permissions ?? defaultPermissionsFor(userId));
  } catch (error) {
    if (!(await isCurrentProfileLoad(userId, generation))) return;
    console.error('Failed to load user permissions:', error);
    useStore.getState().setUserPermissions(defaultPermissionsFor(userId));
  }
}

type AuthSuccessRedirectContext = {
  action: 'SUCCESS';
  isNewRecipeUser: boolean;
  redirectToPath?: string;
};

async function getAuthSuccessRedirectionURL(
  context: AuthSuccessRedirectContext
): Promise<string | undefined> {
  if (environment === 'development') console.log(context);
  if (!(await Session.doesSessionExist())) {
    return undefined;
  }
  const userId = await Session.getUserId();
  if (context.isNewRecipeUser) {
    await loadAuthenticatedUserProfile(userId);
    return '/account';
  }

  try {
    const ssoPath = await resolvePostLoginPath(context.redirectToPath);
    if (ssoPath === clientRoutes.loginReturn) {
      return clientRoutes.loginReturn;
    }
    return context.redirectToPath || ssoPath || '/dashboard';
  } catch (_) {
    clearSsoReturn();
  }

  return context.redirectToPath || '/dashboard';
}

function handleAuthRecipeEvent(context: { action: string; user?: { id: string; emails: string[] } }) {
  if (context.action === 'SESSION_ALREADY_EXISTS') {
    console.log('session already exists');
    return;
  }
  if (context.action !== 'SUCCESS' || !context.user) {
    return;
  }

  const {
    id,
    emails: [email],
  } = context.user;
  void loadAuthenticatedUserProfile(id, email);

  // Opt-in flag only (boolean). Subscribe the authenticated email — never read email from storage.
  // localStorage survives the verification link opening in a new tab; sessionStorage / legacy keys still honoured.
  const newsletterOptIn =
    localStorage.getItem('newsletterOptIn') === '1' ||
    sessionStorage.getItem('newsletterOptIn') === '1' ||
    localStorage.getItem('newsletterSignup') === 'true';

  if (newsletterOptIn) {
    void subscribeToNewsletter(id, email)
      .then(() => {
        console.log('User automatically subscribed to newsletter');
      })
      .catch((error) => {
        console.error('Failed to subscribe user to newsletter:', error);
      })
      .finally(() => {
        localStorage.removeItem('newsletterOptIn');
        sessionStorage.removeItem('newsletterOptIn');
        localStorage.removeItem('newsletterSignup');
        localStorage.removeItem('newsletterEmail');
      });
  } else {
    // Drop any leftover PII from older clients
    localStorage.removeItem('newsletterEmail');
  }
}

SuperTokens.init({
  appInfo: {
    appName: 'my-portfolio',
    apiDomain,
    websiteDomain,
    apiBasePath: '/auth',
    websiteBasePath: '/login',
  },
  getRedirectionURL: async (context) => {
    if (context.action === 'SUCCESS') {
      return getAuthSuccessRedirectionURL(context);
    }
    return undefined;
  },
  recipeList: [
    EmailPassword.init({
      onHandleEvent: handleAuthRecipeEvent,
    }),
    EmailVerification.init({
      mode: 'REQUIRED',
    }),
    // Access token for Unity handoff is exposed via server Session.init
    // exposeAccessTokenToFrontendInCookieBasedAuth — Session.getAccessToken() then works.
    Session.init(),
  ],
});

export const RouteAuth = getSuperTokensRoutesForReactRouterDom(reactRouterDom, [
  EmailPasswordPreBuiltUI,
  EmailVerificationPreBuiltUI,
]);
export { SuperTokensWrapper } from 'supertokens-auth-react';
